import { useEffect, useMemo, useRef, useState } from "react";

import { X } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";

import {
  useUpsertAnnotation,
  type AccountSearchResult,
  type PipelineAnnotation,
  type RenewalSessionRow,
} from "../../api";
import { AccountSearch } from "../account-links/AccountSearch";

const FORECAST_VALUES = ["Unknown", "Renew", "Renewal At Risk", "Churn"] as const;
const RENEWAL_STATUS_VALUES = ["Not Started", "In Progress", "Won", "Lost"] as const;

// Color the trigger by current value so a CSM can scan the pipeline column at
// a glance: green = renewal locked, red = expected churn, yellow = at risk,
// neutral = still unknown.
const FORECAST_TONE: Record<string, string> = {
  Unknown: "",
  Renew: "bg-success-light text-success border-success/30",
  "Renewal At Risk": "bg-warning-light text-warning border-warning/30",
  Churn: "bg-danger-light text-danger border-danger/30",
};

/** Forecast: 4 values. Compact inline select; trigger background reflects the
 *  current pick so the column reads as a heat-map at a glance. */
export function ForecastSelect({
  qbId,
  value,
  disabled,
}: {
  qbId: number;
  value: string | null | undefined;
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const current = value || "Unknown";
  const tone = FORECAST_TONE[current] ?? "";
  return (
    <Select
      value={current}
      disabled={disabled}
      onValueChange={(v) => upsert.mutate({ qbId, payload: { forecast: v } })}
    >
      <SelectTrigger className={cn("h-8 w-full text-xs font-medium", tone)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {FORECAST_VALUES.map((v) => (
          <SelectItem key={v} value={v}>{v}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Outcome → Badge variant. Mirrors AccountDetailContent's OutcomeBadge so the
// Past pipeline cell and the drawer's "Renewal cycles" table read identically.
const OUTCOME_VARIANT: Record<
  string,
  "success" | "danger" | "success-solid" | "warning"
> = {
  renewed: "success",
  churned: "danger",
  expansion: "success-solid",
  contraction: "warning",
};

/** Read-only forecast for a CLOSED (past) renewal cycle. Shows the forecast the
 *  CSM recorded for the cycle that renewed, plus the rollover-inferred outcome.
 *  Used in the "Past" pipeline view, where the editable ForecastSelect would
 *  target the account's *next* (fresh) cycle rather than the one on screen. */
export function PastForecastCell({
  session,
}: {
  session: RenewalSessionRow | null | undefined;
}) {
  if (!session) {
    return (
      <span className="text-xs italic text-muted-foreground">
        No cycle recorded
      </span>
    );
  }
  const forecast = session.forecast || "Unknown";
  const tone = FORECAST_TONE[forecast] ?? "";
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={cn(
          "inline-flex w-fit items-center rounded-md border px-2 py-0.5 text-xs font-medium",
          tone || "border-border text-muted-foreground",
        )}
      >
        {forecast}
      </span>
      {session.outcome && (
        <Badge variant={OUTCOME_VARIANT[session.outcome] ?? "secondary"}>
          {session.outcome}
        </Badge>
      )}
    </div>
  );
}

/** Renewal status: CSM-managed lifecycle. Free-form on the backend; keep the
 *  unknown value as a 5th option so an out-of-set legacy value is still
 *  selectable until the CSM changes it. */
export function RenewalStatusSelect({
  qbId,
  value,
  disabled,
}: {
  qbId: number;
  value: string | null | undefined;
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const current = value || "Not Started";
  const options = useMemo(() => {
    const set = new Set<string>(RENEWAL_STATUS_VALUES);
    if (current && !set.has(current)) set.add(current);
    return Array.from(set);
  }, [current]);
  return (
    <Select
      value={current}
      disabled={disabled}
      onValueChange={(v) => upsert.mutate({ qbId, payload: { renewal_status: v } })}
    >
      <SelectTrigger className="h-8 w-full text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((v) => (
          <SelectItem key={v} value={v}>{v}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** CSM picker. The new backend doesn't have a dedicated /csms endpoint yet
 *  (deferred to PR3 along with the multi-select filters), so the picker pulls
 *  its option set from the pipeline rows the caller already has. We pass the
 *  options in rather than refetching. Unassigned = clear annotation. */
export interface CsmOption {
  user_id: string;
  display_name: string;
}

export function CsmPicker({
  qbId,
  value,
  options,
  disabled,
}: {
  qbId: number;
  value: string | null | undefined;
  options: CsmOption[];
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const UNASSIGNED = "__unassigned__";
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [options],
  );
  return (
    <Select
      value={value ?? UNASSIGNED}
      disabled={disabled}
      onValueChange={(v) =>
        upsert.mutate({
          qbId,
          payload: { csm_user_id: v === UNASSIGNED ? null : v },
        })
      }
    >
      <SelectTrigger className="h-8 w-full text-xs">
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>— Unassigned —</SelectItem>
        {sortedOptions.map((o) => (
          <SelectItem key={o.user_id} value={o.user_id}>
            {o.display_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** CSM assignment: an account is managed by EITHER an internal/HubSpot CSM OR
 *  a partner account (some closed-and-managed contracts pay a partner to run
 *  the customer's Cinch services). A small toggle switches between the person
 *  picker and a partner-account search; the backend enforces mutual exclusivity
 *  (assigning one clears the other). */
export function CsmAssignmentEditor({
  qbId,
  csmUserId,
  csmPartnerQbId,
  csmPartnerName,
  options,
  disabled,
}: {
  qbId: number;
  csmUserId: string | null | undefined;
  csmPartnerQbId: number | null | undefined;
  csmPartnerName: string | null | undefined;
  options: CsmOption[];
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const [mode, setMode] = useState<"internal" | "partner">(
    csmPartnerQbId ? "partner" : "internal",
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex w-fit rounded-md border border-border p-0.5 text-xs">
        {(["internal", "partner"] as const).map((m) => (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => setMode(m)}
            className={cn(
              "rounded px-2 py-0.5 font-medium transition-colors disabled:opacity-50",
              mode === m
                ? "bg-brand-light text-brand"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {m === "internal" ? "Internal CSM" : "Partner"}
          </button>
        ))}
      </div>

      {mode === "internal" ? (
        <CsmPicker
          qbId={qbId}
          value={csmUserId}
          options={options}
          disabled={disabled}
        />
      ) : csmPartnerQbId ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-input bg-background px-2 py-1.5 text-xs">
          <span className="truncate">
            <span className="font-medium">{csmPartnerName ?? `QB-${csmPartnerQbId}`}</span>
            <span className="ml-1 text-muted-foreground">· partner-managed</span>
          </span>
          <button
            type="button"
            disabled={disabled}
            title="Remove partner CSM"
            onClick={() => upsert.mutate({ qbId, payload: { csm_partner_qb_id: null } })}
            className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <AccountSearch
          value={null}
          excludeQb={qbId}
          placeholder="Search partner account…"
          onChange={(r: AccountSearchResult | null) => {
            if (r) upsert.mutate({ qbId, payload: { csm_partner_qb_id: r.qb_id } });
          }}
        />
      )}
    </div>
  );
}

/** Notes field. Auto-saves on blur if changed; debounces typing so a fast
 *  typer doesn't see flicker. Internal draft state means typing is fully
 *  local until save lands. */
export function NotesField({
  qbId,
  value,
  disabled,
  rows = 3,
  className,
}: {
  qbId: number;
  value: string | null | undefined;
  disabled?: boolean;
  rows?: number;
  className?: string;
}) {
  const upsert = useUpsertAnnotation();
  const [draft, setDraft] = useState(value ?? "");
  // Re-sync if the server value changes underneath us (e.g. SSE event from
  // another tab) AND the user isn't actively editing.
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!dirtyRef.current) setDraft(value ?? "");
  }, [value]);
  return (
    <textarea
      className={cn(
        "block w-full resize-y rounded-md border border-input bg-background px-2 py-1 text-xs disabled:opacity-50",
        className,
      )}
      value={draft}
      rows={rows}
      placeholder="Notes…"
      disabled={disabled}
      onChange={(e) => {
        dirtyRef.current = true;
        setDraft(e.target.value);
      }}
      onBlur={() => {
        if (draft !== (value ?? "")) {
          upsert.mutate({ qbId, payload: { notes: draft || null } });
        }
        dirtyRef.current = false;
      }}
    />
  );
}

/** Renewal anchor picker. Three anchor strategies + a custom override date.
 *  Listed for completeness — used by the expanded view in PR3's polish. */
const ANCHOR_OPTIONS = ["global", "billing", "contract", "custom"] as const;

export function AnchorEditor({
  qbId,
  annotation,
  disabled,
}: {
  qbId: number;
  annotation: Pick<PipelineAnnotation, "renewal_anchor" | "renewal_date_override">;
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const anchor = annotation.renewal_anchor || "global";
  return (
    <div className="flex flex-col gap-2 text-xs">
      <Select
        value={anchor}
        disabled={disabled}
        onValueChange={(v) =>
          upsert.mutate({
            qbId,
            payload: {
              renewal_anchor: v,
              // Switching off custom clears the override date.
              renewal_date_override: v === "custom" ? annotation.renewal_date_override : null,
            },
          })
        }
      >
        <SelectTrigger className="h-8 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ANCHOR_OPTIONS.map((v) => (
            <SelectItem key={v} value={v}>{v}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {anchor === "custom" && (
        <input
          type="date"
          className="h-8 rounded-md border border-input bg-background px-2"
          disabled={disabled}
          value={annotation.renewal_date_override ?? ""}
          onChange={(e) =>
            upsert.mutate({
              qbId,
              payload: { renewal_date_override: e.target.value || null },
            })
          }
        />
      )}
    </div>
  );
}
